import { AuthError, isAuthFailureCode } from "./auth-error.js";
import type { WorkerRequest, WorkerResponse } from "./types.js";

type RequestPayload = WorkerRequest extends infer Request
  ? Request extends { readonly id: number }
    ? Omit<Request, "id">
    : never
  : never;

function workerError(response: WorkerResponse): Error {
  const message = response.error ?? "Vault worker failed";
  return isAuthFailureCode(response.errorCode) ? new AuthError(response.errorCode, message) : new Error(message);
}

export class VaultWorkerClient {
  readonly #worker = new Worker(new URL("./vault-worker.ts", import.meta.url), { type: "module", name: "recallvault-core" });
  readonly #pending = new Map<number, { resolve: (value: unknown) => void; reject: (error: Error) => void; timer?: ReturnType<typeof setTimeout>; init: boolean }>();
  #nextId = 1;
  #failure: Error | undefined;

  constructor() {
    this.#worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
      const pending = this.#pending.get(event.data.id);
      if (!pending) return;
      if (event.data.initStage) {
        clearTimeout(pending.timer);
        pending.timer = setTimeout(() => {
          this.close(new Error(`Vault initialization stalled during ${event.data.initStage}. Try again; cached data has not been deleted.`));
        }, 6_000);
        return;
      }
      this.#pending.delete(event.data.id);
      clearTimeout(pending.timer);
      if (event.data.ok) pending.resolve(event.data.value);
      else { const error = workerError(event.data); pending.reject(error); if (pending.init) this.close(error); }
    });
    this.#worker.addEventListener("error", (event) => {
      this.close(new Error(event.message || "The vault worker could not start. Try again."));
    });
    this.#worker.addEventListener("messageerror", () => {
      this.close(new Error("The vault worker returned an unreadable response. Try again."));
    });
  }

  /**
   * The failure that closed this worker, once one has.
   *
   * The latch is permanent by design, so this is the difference between "that
   * one call failed" and "nothing will work again until this page reloads" —
   * which is what decides whether a rejection deserves a toast or the terminal
   * state (`fatal-state.ts`).
   */
  get failure(): Error | undefined {
    return this.#failure;
  }

  request<T>(payload: RequestPayload): Promise<T> {
    if (this.#failure) return Promise.reject(this.#failure);
    const id = this.#nextId++;
    return new Promise<T>((resolve, reject) => {
      const timer = payload.type === "init" ? setTimeout(() => {
        this.close(new Error("Opening your cached vault took too long. Try again; your saved data has not been deleted."));
      }, 20_000) : undefined;
      this.#pending.set(id, { resolve: resolve as (value: unknown) => void, reject, timer, init: payload.type === "init" });
      try {
        this.#worker.postMessage({ id, ...payload } satisfies WorkerRequest);
      } catch (error) {
        clearTimeout(timer);
        this.#pending.delete(id);
        reject(error instanceof Error ? error : new Error("The vault worker could not receive the request."));
      }
    });
  }

  close(error = new Error("Vault worker closed")): void {
    this.#failure = error;
    this.#worker.terminate();
    for (const pending of this.#pending.values()) { clearTimeout(pending.timer); pending.reject(error); }
    this.#pending.clear();
  }
}
